import { ICrudLocale } from '@app/i18n/interface';

/** Bookings texts. Same keys as `es.ts`. */
export const ORDER_MANAGEMENT_LOCALE_EN: ICrudLocale = {
  ORDER: {
    LABEL: {
      SINGULAR: 'Booking',
      PLURAL: 'Bookings',
    },
    FIELD: {
      REFERENCE: 'Reference',
      NO_NUMBER: 'No number (not charged)',
      NO_NUMBER_SHORT: 'No number',
      CUSTOMER: 'Customer',
      CUSTOMER_GUEST: 'Guest',
      HOLDER: 'Holder',
      EMAIL: 'Email',
      STATUS: 'Status',
      TOTAL: 'Total',
      CURRENCY: 'Currency',
      CREATED_AT: 'Created',
      UPDATED_AT: 'Updated',
      HOTEL: 'Hotel',
      CHECK_IN: 'Check-in',
      INTERNAL_NOTES: 'Internal notes',
      DEADLINE: 'Payment deadline',
      AMOUNT_DUE: 'Amount due',
    },
    PROVIDER_ALERTS: {
      TITLE: 'Bookings with Hotelbeds or Stripe problems',
      SUBTITLE: 'What the Hotelbeds and Stripe checks found and is still unresolved.',
      MISSING: 'Hotelbeds does not have it',
      MISSING_ACTION: 'We consider it confirmed and the hotel will not find it: rebook it or tell the customer before they travel.',
      ORPHAN: 'Still live at Hotelbeds',
      ORPHAN_ACTION: 'We consider it cancelled and Hotelbeds keeps it: cancel it with them or claim, or we will be invoiced.',
      CANCEL_FAILED: 'Cancellation failed',
      CANCEL_FAILED_ACTION: 'An unpaid booking that could not be cancelled: it is still live at Hotelbeds. Cancel it before the cancellation fees start, or IBSOA pays them.',
      PAYMENT_STUCK: 'Payment stuck in Stripe',
      PAYMENT_STUCK_ACTION: 'The Stripe payment could not be closed. Do NOT cancel it by hand at Hotelbeds: sort it out with Stripe first. If the payment goes through, the alert clears itself.',
      CANCEL_CHARGED: 'Hotelbeds charged the cancellation',
      CANCEL_CHARGED_ACTION: 'Hotelbeds charged for cancelling an unpaid booking; IBSOA bears it. The amount is in the booking detail.',
      WHEN_TODAY: 'Checks in today',
      WHEN_TOMORROW: 'Checks in tomorrow',
      WHEN_DAYS: 'Checks in in {{days}} days',
      WHEN_PAST: 'Checked in {{days}} days ago',
      REF: 'Hotelbeds reference {{ref}}',
      NO_REF: 'No Hotelbeds reference',
      DETECTED: 'detected on',
      NO_HOTEL: 'Unnamed hotel',
      FAILED_TITLE: 'Could not check the bookings with problems',
      FAILED_DESC: 'The list did not load, so we do not know whether there are any. Reload the page; if it persists, check the API.',
    },
    LIST: {
      TITLE: 'All bookings',
      DESCRIPTION: 'Every booking made on the website, with its payment and Hotelbeds confirmation status.',
      CAPTION: 'Bookings',
      FILTERS: 'Filter bookings',
      REFERENCE: 'Reference',
      REFERENCE_PLACEHOLDER: 'IBSOA-12345678',
      CUSTOMER: 'Customer',
      CUSTOMER_PLACEHOLDER: 'Name or email',
      CUSTOMER_NOT_FOUND: 'No customer with that name or email.',
      CREATED_FROM: 'Created from',
      CREATED_TO: 'Created to',
      DATE_PLACEHOLDER: 'dd/mm/yyyy',
      DATE_INVALID: 'Write the date as dd/mm/yyyy.',
      APPLY: 'Apply filters',
      CLEAR: 'Clear',
      STATUS_FILTER: 'Filter by status',
      CHIP: {
        ALL: 'All',
        CONFIRMED: 'Confirmed',
        PENDING_PAYMENT: 'Pending payment',
        BOOKED_UNPAID: 'Booked, unpaid',
        FAILED: 'Failed',
        PARTIALLY_CONFIRMED: 'Partially confirmed',
        CANCELLED: 'Cancelled',
        REFUNDED: 'Refunded',
        EXPIRED: 'Expired',
        CARTS: 'Carts',
      },
      SHOW_CARTS: 'Also show carts',
      CARTS_NOTE: '“All” does not include carts: website baskets nobody has paid.',
      NO_DATA: 'No bookings match these filters.',
      EXPORT_CSV: 'Export CSV',
      PAY_BY: 'Pay by {{date}}',
      HOTEL_TIME: 'hotel time',
      AMOUNT_DUE: 'Due: {{amount}}',
      // Colour alone is not enough: it is also said (visual QA 31-10).
      DUE_SOON: 'Due in less than 24 h',
    },
    REASON: {
      PAYMENT_FAILED: 'Payment not completed',
      BOOKING_FAILED_CHARGED: 'Charged with no Hotelbeds booking',
      BOOKING_FAILED_REFUND_PENDING: 'No Hotelbeds booking, refund in progress',
      BOOKING_FAILED_PARTIALLY_REFUNDED: 'No Hotelbeds booking, partially refunded',
      BOOKING_FAILED_REFUNDED: 'No Hotelbeds booking, charge refunded',
      ROOMS_UNCONFIRMED: '{{count}} of {{total}} rooms not confirmed',
      EXPIRED: 'Not paid in time',
      BOOKED_UNPAID: 'Not charged: the customer pays before the deadline',
      CANCELLATION_IN_PROGRESS: 'Cancellation in progress, at no cost',
    },
    PAY_LATER: {
      TITLE: 'Pay later',
      DESCRIPTION: 'Booked at Hotelbeds without charging. If the customer does not pay before the deadline, it is cancelled automatically at no cost.',
      DEADLINE: 'Payment deadline',
      HOTEL_TIME: 'hotel time',
      UTC: '{{date}} UTC',
      AMOUNT_DUE: 'Amount due',
      NOTHING_DUE: 'Nothing due',
      CANCELLATION: 'Cancellation in progress',
      CANCELLATION_SINCE: 'Requested on {{date}}. The automatic process completes it at no cost; it can no longer be paid.',
      REMINDER_72: '72-hour reminder',
      REMINDER_24: '24-hour reminder',
      REMINDER_NOT_SENT: 'Not sent',
    },
    TIMELINE: {
      TITLE: 'History',
      DESCRIPTION: 'What happened to the booking, newest first.',
      TIMEZONE: 'Times in your time zone ({{zone}})',
      EMPTY: 'Nothing in this booking’s history yet.',
      BY: 'By {{actor}}',
      SYSTEM: 'By the automatic process',
      NO_DATE: 'No date recorded',
      ROOMS: 'Room {{rooms}}',
      ROOM_UNKNOWN: 'A room of the booking',
      NO_REFERENCE: 'Hotelbeds has no reference for this room.',
      REFERENCE_FOUND: 'Hotelbeds reference: {{ref}}',
      EXPECTED: 'We expected reference {{ref}}.',
      FOUND: 'Hotelbeds has: {{refs}}',
      REFUND_ID: 'Stripe refund {{id}}',
      STATUS_TO: 'The booking becomes “{{status}}”',
      AMOUNT: 'Amount: {{amount}}',
      REPRICE_AMOUNT: 'Cost difference: {{amount}}',
      PROVIDER_FAILED: 'Hotelbeds returned an error',
      PROVIDER_STATUS: '{{operation}}: response {{status}}',
      PROVIDER_NO_RESPONSE: '{{operation}}: no response',
      PROVIDER_OPEN: 'See the Hotelbeds response',
      EVENT: {
        ORDER_CREATED: 'Booking created on the website',
        PAYMENT_INITIATED: 'Payment started',
        STATUS_CHANGE: 'Status change',
        CONFIRMATION_SENT: 'Confirmation sent',
        CONFIRMATION_RESENT: 'Confirmation resent',
        NOTES_UPDATE: 'Internal notes updated',
        REFUND: 'Refund',
        REPRICE_ACCEPTED: 'Hotelbeds adjusted the price when booking',
        BOOKING_RECHECK: 'Booking rechecked at Hotelbeds',
        BOOKING_MISSING: 'Hotelbeds does not have the booking',
        BOOKING_ORPHAN: 'Still live at Hotelbeds',
        PAYMENT_SUCCEEDED: 'Payment completed',
        BOOKING_CONFIRMED: 'Hotelbeds confirmed the booking',
        BOOKING_FAILED: 'The booking failed at Hotelbeds',
      },
    },
    DETAIL: {
      TITLE: 'Booking detail',
      TITLE_REFERENCE: 'Booking {{reference}}',
      TITLE_NO_REFERENCE: 'Booking without number',
      CREATED: 'Created on {{date}} ({{zone}} time).',
      NOT_FOUND: 'The booking was not found.',
      BOOKED_AT: 'Booking date',
      PLACED_AT: 'Went to payment',
      PAID_AT: 'Paid',
      CONFIRMED_AT: 'Confirmed by Hotelbeds',
      BACK: 'Back to bookings',
      SUMMARY: 'Customer',
      NO_TRAVELERS: 'No guests recorded.',
    },
    DOCUMENTS: {
      TITLE: 'Voucher and receipt',
      DESCRIPTION: 'The same documents the customer gets.',
      LOADING: 'Checking the documents…',
      VOUCHER: 'Voucher',
      RECEIPT: 'Receipt',
      VIEW_VOUCHER: 'View voucher',
      DOWNLOAD_PDF: 'Download PDF',
      VIEW_RECEIPT: 'View receipt',
      PDF_OFF: 'The PDF is not available in this environment; the voucher is.',
      REASON: {
        PENDING_PAYMENT: 'Payment pending: no voucher or receipt until it is charged.',
        NO_CONFIRMED_BOOKING: 'No room is confirmed with a booking reference.',
        NOT_PAID: 'No receipt: the booking is not recorded as paid.',
        LINK_EXPIRED: 'The voucher link expired (30 days after check-out).',
        NOT_CONFIGURED: 'This environment has no website configured.',
      },
      FAILED: {
        TITLE: 'Could not check the documents',
        DESC: 'Reload the page; if it keeps failing, tell the tech team.',
      },
    },
    RECEIPT: {
      TITLE: 'Payment receipt',
      BACK: 'Back to the booking',
      REFERENCE: 'Reference',
      ORDERED_AT: 'Order date',
      PAID_AT: 'Payment date',
      HOLDER: 'Holder',
      ISSUER: 'Issuer',
      PAYMENT_REFERENCE: 'Payment reference',
      LINES: 'What was paid',
      STAY: 'From {{checkIn}} to {{checkOut}} · {{nights}} nights',
      INCLUDED: 'Taxes and fees included',
      AT_HOTEL: 'Payable at the property, separately: {{amount}}',
      AT_HOTEL_UNKNOWN: 'There are charges payable at the property, separately, of unknown amount.',
      FREE_CANCELLATION: 'Free cancellation until {{until}} (property local time).',
      NON_REFUNDABLE: 'No free cancellation.',
      TOTAL: 'Total paid',
      REFUNDED: 'Refunded',
      FAILED: {
        TITLE: 'Could not load the receipt',
        DESC: 'The booking may not be recorded as paid yet. Go back to the booking and check its payment.',
      },
    },
    ORIGIN: {
      TITLE: 'Source',
      DESCRIPTION: 'Where the visit that ended in this purchase came from. For administration only.',
      SOURCE: 'Source',
      MEDIUM: 'Medium',
      CAMPAIGN: 'Campaign',
      TERM: 'Term',
      CONTENT: 'Content',
      LANDING_PATH: 'Landing page',
      REFERRER_HOST: 'Referring site',
      AD_CLICK: 'Ad click',
      NO_AD_CLICK: 'None',
      CLICK_IDS_PURGED: 'The click identifiers were already deleted after the retention period.',
      CLICKED_AT: 'Clicked at',
      CAPTURED_AT: 'Recorded at',
      NONE: 'No source data',
      NONE_WHY: 'Bookings before 2 November 2026 and those from visitors who did not accept cookies do not have it.',
      CLICK_ID: {
        GCLID: 'Google Ads',
        GBRAID: 'Google Ads (iOS)',
        WBRAID: 'Google Ads (web to app)',
        FBCLID: 'Meta',
      },
    },
    INTERNAL: {
      TITLE: 'Internal amounts',
      DESCRIPTION: 'Cost and margin, for administration only.',
      NET_AMOUNT: 'Net (cost)',
      MARKUP_AMOUNT: 'Margin',
      FEES_AMOUNT: 'Fees',
    },
    PAYMENT: {
      TITLE: 'Payment (Stripe)',
      STATUS: 'Payment status',
      AMOUNT: 'Amount',
      INTENT_ID: 'Identifier',
      NONE: 'No payment recorded.',
    },
    ITEMS: {
      TITLE: 'Hotel and rooms',
      ROOM: 'Room {{n}}',
      CHECK_IN: 'Check-in',
      CHECK_OUT: 'Check-out',
      NIGHTS: '{{n}} nights',
      PROVIDER: 'Provider',
      BOOKING_REF: 'Hotelbeds reference',
      HOTEL_CONFIRMATION: 'Hotel confirmation no.',
      ROOM_NAME: 'Room',
      BOARD_NAME: 'Board (boardName)',
      RATE_COMMENTS: 'Rate comments (from CheckRate)',
      RAW_NOTE: 'Texts in grey boxes are exactly what Hotelbeds sends, untranslated.',
      SALE_PRICE: 'Sale price',
      TRAVELERS: 'Guests',
      NONE: 'This booking has no rooms.',
      UNAVAILABLE: 'Hotelbeds stopped offering this rate on {{date}}.',
      FAILED_TITLE: 'Hotelbeds did not confirm this room',
      FAILED_CALL: '{{operation}}: response {{status}} on {{date}}.',
      FAILED_NO_RESPONSE: '{{operation}}: no response on {{date}}.',
      PROVIDER_ERROR_AT: 'Hotelbeds response of {{date}}.',
      FAILED_OPEN: 'See the Hotelbeds response',
      FAILED_LOADING: 'Looking for the reason in the Hotelbeds calls…',
      FAILED_UNKNOWN: 'No failed call on record explains it. Check this booking’s calls.',
      FAILED_LOOKUP_ERROR: 'Could not read the Hotelbeds calls to find the reason.',
      CANCEL_CHARGED_TITLE: 'Hotelbeds charged for cancelling this room',
      CANCEL_CHARGED_DESC: 'Charge of {{amount}} for cancelling an unpaid booking. IBSOA bears it: it is not passed on to the customer.',
    },
    TRAVELER: {
      NAME: 'Name',
      TYPE: 'Type',
      AGE: 'Age',
      AGE_VALUE: '{{age}} years',
      TYPE_AD: 'adult',
      TYPE_CH: 'child',
    },
    STATUS: {
      CANCELLED: 'Cancelled',
      CART: 'Cart',
      COMPLETED: 'Completed',
      CONFIRMED: 'Confirmed',
      FAILED: 'Failed',
      PAID: 'Paid',
      PARTIALLY_CONFIRMED: 'Partially confirmed',
      PENDING_PAYMENT: 'Pending payment',
      REFUNDED: 'Refunded',
      EXPIRED: 'Expired',
      BOOKED_UNPAID: 'Booked, pending payment',
    },
    ITEM_STATUS: {
      CANCELLED: 'Cancelled',
      COMPLETED: 'Completed',
      CONFIRMED: 'Confirmed',
      FAILED: 'Failed',
      PARTIALLY_REFUNDED: 'Partially refunded',
      PENDING: 'Pending',
      REFUNDED: 'Refunded',
    },
    PAYMENT_STATUS: {
      FAILED: 'Failed',
      PARTIALLY_REFUNDED: 'Partially refunded',
      PROCESSING: 'Processing',
      REFUNDED: 'Refunded',
      REFUND_PENDING: 'Refund pending',
      REQUIRES_PAYMENT: 'Unpaid',
      SUCCEEDED: 'Charged',
    },
    VERTICAL: {
      CRUISE: 'Cruise',
      FLIGHT: 'Flight',
      HOTEL: 'Hotel',
      TRANSFER: 'Transfer',
    },
    ACTION: {
      TITLE: 'Actions',
      DESCRIPTION: 'What you can do with this booking.',
      STATUS: {
        TITLE: 'Change status',
        PLACEHOLDER: 'Choose the new status',
        SUBMIT: 'Change status',
        CONFIRM_TITLE: 'Change the booking status?',
        CONFIRM_TEXT: 'The booking will become “{{status}}”.',
        SUCCESS: 'Booking status updated.',
      },
      REFUND: {
        TITLE: 'Manual refund',
        LABEL: 'Amount to refund ({{currency}})',
        HINT: 'Up to {{max}}.',
        PLACEHOLDER: '0.00',
        SUBMIT: 'Refund',
        CONFIRM_TITLE: 'Refund {{amount}}?',
        CONFIRM_TEXT: '<strong>{{amount}}</strong> will be returned to the customer’s card. This cannot be undone.',
        CONFIRM_BUTTON: 'Refund',
        SUCCESS: 'Refund issued.',
      },
      NOTES: {
        PLACEHOLDER: 'Only the team sees them',
        SUBMIT: 'Save notes',
        SUCCESS: 'Internal notes saved.',
      },
      RESEND: {
        TITLE: 'Voucher',
        HINT: 'Resends the confirmation email to the customer, with the link to their voucher.',
        SUBMIT: 'Resend confirmation and voucher',
        CONFIRM_TITLE: 'Resend the confirmation and voucher?',
        CONFIRM_TEXT: 'The confirmation email will be resent to “{{recipient}}”.',
        CONFIRM_TEXT_CONTACT: 'The confirmation email will be resent to the booking’s contact address.',
        CONFIRM_BUTTON: 'Resend',
        SUCCESS: 'Confirmation resent.',
      },
      CANCEL: {
        TITLE: 'Cancel the booking',
        // Deferred payment with cancellation already requested: the API would answer ORDER_CANCELLATION_PENDING.
        IN_PROGRESS: 'The cancellation is already in progress: the automatic process completes it, at no cost. There is no need to cancel it from here.',
        COST_TODAY: 'Cost of cancelling today',
        NO_COST: 'No cost',
        SERVICE_FEE: 'IBSOA handling fee',
        SERVICE_FEE_NONE: 'Not applied when cancelling from here',
        REFUND: 'Amount to refund',
        REFUND_PENDING: 'to be defined',
        REFUND_NONE: 'Nothing: it was not charged',
        FREE_UNTIL: 'Free cancellation until',
        NO_FREE: 'No free cancellation',
        UTC: '{{date}} UTC',
        LOCAL_NOTE: 'local time at the property',
        NOTE: 'Calculated with the cancellation policy stored on each room. The amount is confirmed when cancelling.',
        LOADING: 'Calculating the cost of cancelling…',
        RETRY: 'Recalculate',
        SUBMIT: 'Cancel booking',
        CONFIRM_TITLE: 'Cancel booking {{reference}}?',
        CONFIRM_TEXT: 'Cost of cancelling today: <strong>{{cost}}</strong><br>Amount to refund: <strong>to be defined</strong><br><br>It is cancelled at Hotelbeds. This cannot be undone.',
        CONFIRM_TEXT_UNPAID: 'The booking was not charged: it is cancelled at Hotelbeds at no cost and there is nothing to refund.<br><br>This cannot be undone.',
        CONFIRM_BUTTON: 'Cancel the booking',
        KEEP: 'Keep the booking',
        SUCCESS: 'Booking cancelled.',
      },
      ERROR: {
        GENERIC: 'The action could not be completed. Try again.',
        CONFLICT: 'The action is not valid for the booking’s current status.',
        INVALID: 'The action data are not valid.',
        INVALID_AMOUNT: 'The amount must be greater than 0 and not exceed the refundable maximum.',
        REFUND_EXCEEDS_PAYMENT: 'The amount exceeds what can be refunded from the payment.',
        QUOTE: 'Could not calculate the cost of cancelling.',
        NO_RECIPIENT: 'The booking has no email to send the confirmation to.',
        RATE_LIMITED: 'The confirmation was resent recently. Wait a little before retrying.',
        NOT_FOUND: 'The booking was not found.',
        STATE_CHANGED: 'The booking changed while you were looking at it (a payment or the automatic process). Reload it and try again.',
        CANCELLATION_PENDING: 'The cancellation is already in progress: the automatic process completes it at no cost.',
        CANCELLATION_FAILED: 'Could not cancel right now: Hotelbeds or Stripe did not respond and nothing was cancelled. Try again later.',
      },
    },
  },
};
